import { useEffect, useState } from "react";

import LiveChatFooter from "components/helper/LiveChatFooter";
import ExtensionBanner from "components/ui/ExtensionBanner";
import ProspectHeatmap from "./components/ProspectHeatmap";
import CombinedLineProspects from "components/dashboard/CombinedLineProspects";
import ChartBar3Period from "components/dashboard/ChartBar3Period";
import { getProspectMonthlyLogs, getProspectsByStatus } from "interfaces/prospect";
import { getUserGoals } from "interfaces/user";
import { FaFire, FaTrophy, FaBullseye, FaRocket } from "react-icons/fa6";
import ReplyRatesByTag from "views/analytics/ReplyRatesByTag";

const Home = (props: { print?: boolean }) => {
    const { print } = props;
    const [contactedData, setContactedData] = useState<any[]>([]);
    const [responsesData, setResponsesData] = useState<any[]>([]);
    const [, setContactedPeriod] = useState("weekly");
    const [, setResponsesPeriod] = useState("weekly");
    const [statusData, setStatusData] = useState<any[]>([]);
    const [todayContacts, setTodayContacts] = useState<number>(0);
    const [weeklyContacts, setWeeklyContacts] = useState<number>(0);
    const [weeklyResponses, setWeeklyResponses] = useState<number>(0);
    const [monthlyDeals, setMonthlyDeals] = useState<number>(0);
    const [userGoals, setUserGoals] = useState({
        dailyContacts: 1,
        weeklyContacts: 5,
        weeklyResponses: 2,
        monthlyDeals: 1
    });

    const loadContactedData = async (period: string) => {
        const contacted = await getProspectMonthlyLogs({
            project: null,
            types: "send-email,send-linkedin",
            period: period,
        });
        setContactedData(Array.isArray(contacted) ? contacted : []);
    };

    const loadResponsesData = async (period: string) => {
        const responses = await getProspectMonthlyLogs({
            project: null,
            types: "received-email,received-linkedin",
            period: period,
        });
        setResponsesData(Array.isArray(responses) ? responses : []);
    };

    const handleContactedPeriodChange = (period: string) => {
        setContactedPeriod(period);
        loadContactedData(period);
    };

    const handleResponsesPeriodChange = (period: string) => {
        setResponsesPeriod(period);
        loadResponsesData(period);
    };

    const loadStatusData = async () => {
        const project = localStorage.getItem("project-id");
        const statusStats = await getProspectsByStatus({
            project: project || undefined,
        });
        setStatusData(Array.isArray(statusStats) ? statusStats : []);
    };

    const loadTodayContacts = async () => {
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
        const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();

        const contacted = await getProspectMonthlyLogs({
            project: null,
            types: "send-email,send-linkedin",
            from: todayStart,
            to: todayEnd,
        });

        setTodayContacts(contacted[0]?.sum || 0);
    };

    const loadWeeklyContacts = async () => {
        const today = new Date();
        const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ...
        const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1; // Adjust for Monday start
        
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - daysFromMonday);
        weekStart.setHours(0, 0, 0, 0);
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 7);

        const contacted = await getProspectMonthlyLogs({
            project: null,
            types: "send-email,send-linkedin",
            from: weekStart.toISOString(),
            to: weekEnd.toISOString(),
        });

        setWeeklyContacts(contacted.reduce((sum: any, item: any) => sum + (item.sum || 0), 0));
    };

    const loadWeeklyResponses = async () => {
        const today = new Date();
        const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ...
        const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1; // Adjust for Monday start

        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - daysFromMonday);
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 7);

        const responses = await getProspectMonthlyLogs({
            project: null,
            types: "received-email,received-linkedin",
            from: weekStart.toISOString(),
            to: weekEnd.toISOString(),
        });

        setWeeklyResponses(responses.reduce((sum: any, item: any) => sum + (item.sum || 0), 0));
    };

    const loadMonthlyDeals = async () => {
        // For monthly deals, we'll use the current "won" count from status data
        // This represents total deals closed, which is appropriate for a monthly goal
        const project = localStorage.getItem("project-id");
        const statusStats = await getProspectsByStatus({
            project: project || undefined,
        });
        
        const wonDealsThisMonth = statusStats.find((item: any) => item.status === "won");
        setMonthlyDeals(wonDealsThisMonth ? wonDealsThisMonth.count : 0);
    };

    const loadUserGoals = async () => {
        const goalsRes: any = await getUserGoals();
        if (goalsRes?.data) {
            setUserGoals({
                dailyContacts: goalsRes.data.dailyContacts || 1,
                weeklyContacts: goalsRes.data.weeklyContacts || 5,
                weeklyResponses: goalsRes.data.weeklyResponses || 2,
                monthlyDeals: goalsRes.data.monthlyDeals || 1
            });
        }
    };

    const getStatusCount = (status: string) => {
        const statusItem = statusData.find((item) => item.status === status);
        return statusItem ? statusItem.count : 0;
    };

    // Gamification logic using real chart data
    const getGamificationData = () => {
        // Use the same data structure as charts for real-time accuracy
        const newProspects = getStatusCount("new");
        const contactedProspects = getStatusCount("contacted");
        const qualifiedProspects = getStatusCount("qualified");
        const wonDeals = getStatusCount("won");
        
        // Calculate total activity from all periods (cumulative like charts)
        const totalContactsAllTime = contactedData.reduce((sum, item) => sum + (item.count || 0), 0);
        const totalResponsesAllTime = responsesData.reduce((sum, item) => sum + (item.count || 0), 0);
        
        // Use actual today's contacts, this week's contacts, and this week's responses
        const dailyContacts = todayContacts;
        const actualWeeklyContacts = weeklyContacts;
        const actualWeeklyResponses = weeklyResponses;
        
        const achievements = [
            {
                title: "Daily Goal",
                description: `Contact ${userGoals.dailyContacts}+ prospects daily`,
                current: dailyContacts,
                target: userGoals.dailyContacts,
                icon: <FaFire className="w-4 h-4" />,
                color: "text-orange-600",
                bgColor: "bg-orange-50",
                borderColor: "border-orange-200",
                level: "Beginner",
                metric: `${todayContacts} contacted today`,
                total: `${totalContactsAllTime} total`
            },
            {
                title: "Weekly Goal",
                description: `Contact ${userGoals.weeklyContacts}+ prospects weekly`,
                current: actualWeeklyContacts,
                target: userGoals.weeklyContacts,
                icon: <FaTrophy className="w-4 h-4" />,
                color: "text-blue-600",
                bgColor: "bg-blue-50",
                borderColor: "border-blue-200",
                level: "Intermediate",
                metric: `${weeklyContacts} contacted this week`,
                total: `${totalContactsAllTime} total`
            },
            {
                title: "Response Magnet",
                description: `Get ${userGoals.weeklyResponses}+ responses weekly`,
                current: actualWeeklyResponses,
                target: userGoals.weeklyResponses,
                icon: <FaBullseye className="w-4 h-4" />,
                color: "text-green-600",
                bgColor: "bg-green-50",
                borderColor: "border-green-200",
                level: "Advanced",
                metric: `${weeklyResponses} responses this week`,
                total: `${totalResponsesAllTime} total responses`
            },
            {
                title: "Deal Closer",
                description: `Close ${userGoals.monthlyDeals}+ deals monthly`,
                current: monthlyDeals,
                target: userGoals.monthlyDeals,
                icon: <FaRocket className="w-4 h-4" />,
                color: "text-purple-600",
                bgColor: "bg-purple-50",
                borderColor: "border-purple-200",
                level: "Expert",
                metric: `${monthlyDeals} deals closed`,
                total: `${contactedProspects} in pipeline`
            }
        ];

        return { achievements, stats: { newProspects, contactedProspects, qualifiedProspects, wonDeals } };
    };

    const renderGamificationMetrics = () => {
        const { achievements } = getGamificationData();

        return (
            <div className="mt-5 w-full">
                {/* Achievement Cards - matching app's card style */}
                <div className="col-span-12 grid h-full w-full grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 mb-5">
                    {achievements.map((achievement: any, index: number) => {
                        const progress = Math.min((achievement.current / achievement.target) * 100, 100);
                        const isCompleted = achievement.current >= achievement.target;

                        return (
                            <div key={index} className="flex w-full border items-start gap-[5px] rounded bg-clip-border px-[18px] py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none bg-white">
                                <div className="w-full">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className={`p-2 rounded-lg ${achievement.bgColor} ${achievement.color} ${achievement.borderColor} border`}>
                                            {achievement.icon}
                                        </div>
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            isCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                            {achievement.level}
                                        </span>
                                    </div>

                                    <h4 className="text-sm font-bold leading-6 text-navy-700 dark:text-white mb-1">
                                        {achievement.title}
                                    </h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-gray-600">{achievement.description}</span>
                                            <span className={`font-medium ${isCompleted ? 'text-green-600' : achievement.color}`}>
                                                {achievement.current}/{achievement.target}
                                            </span>
                                        </div>

                                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                                            <div
                                                className={`h-1.5 rounded-full transition-all duration-500 ${
                                                    isCompleted ? 'bg-green-500' : achievement.color.replace('text-', 'bg-')
                                                }`}
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    useEffect(() => {
        loadContactedData("weekly");
        loadResponsesData("weekly");
        loadStatusData();
        loadTodayContacts();
        loadWeeklyContacts();
        loadWeeklyResponses();
        loadMonthlyDeals();
        loadUserGoals();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!print) return;
            window.print();
        }, 2000);
        return () => clearTimeout(timer);
    }, [print]);

    return (
        <>
            <div className="mb-4">
                <ExtensionBanner />
            </div>
            
            {/* Gamification Metrics */}
            {renderGamificationMetrics()}
            
            <div className="mt-5 grid w-full grid-cols-8 gap-5">
                <div className="col-span-12">
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                         <div className="col-span-4">
                            <ProspectHeatmap />
                        </div>
                        <div className="col-span-4">
                            <CombinedLineProspects
                                contacted={contactedData}
                                responses={responsesData}
                                onPeriodChange={(p) => {
                                    handleContactedPeriodChange(p);
                                    handleResponsesPeriodChange(p);
                                }}
                            />
                        </div>
                        <div className="col-span-2">
                            <ChartBar3Period
                                title={"Prospects contacted"}
                                data={contactedData}
                                calculateCumulativeSum={true}
                                onPeriodChange={handleContactedPeriodChange}
                            />
                        </div>
                        <div className="col-span-2">
                            <ChartBar3Period
                                title={"Responses received"}
                                data={responsesData}
                                calculateCumulativeSum={true}
                                onPeriodChange={handleResponsesPeriodChange}
                            />
                        </div>
                    </div>
                    <div className="mt-10">
                     <ReplyRatesByTag />
                     </div>
                </div>
            </div>
            <LiveChatFooter />
        </>
    );
};

export default Home;
